import React from 'react';
import { Bot } from 'lucide-react';
import { colors } from '../tokens';
import { LoadingProps } from '../types';
import { generateId, announceToScreenReader } from '../utils/accessibility';

interface LoadingSpinnerProps extends LoadingProps {
  /** The size of the loading spinner */
  size?: 'sm' | 'md' | 'lg';
  /** Custom aria-label for accessibility */
  'aria-label'?: string;
}

/**
 * LoadingSpinner Component
 * 
 * A customizable loading spinner component that displays a spinning bot icon.
 * Supports different sizes, overlay mode, and accessibility features.
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <LoadingSpinner />
 * 
 * // With overlay
 * <LoadingSpinner overlay={true} text="Loading data..." />
 * 
 * // Custom size and accessibility
 * <LoadingSpinner size="lg" aria-label="Loading user profile" />
 * ```
 * 
 * @param props - The component props
 * @param props.size - The size of the spinner ('sm', 'md', 'lg')
 * @param props.overlay - Whether to show as full-screen overlay
 * @param props.text - Text to display with the spinner (screen reader only)
 * @param props['aria-label'] - Custom aria-label for accessibility
 * @param props.className - Additional CSS classes
 * @param props.style - Additional inline styles
 * @param props.id - Unique identifier
 * @param props['data-testid'] - Test identifier
 * 
 * @returns A React component that renders a loading spinner
 */
export default function LoadingSpinner({ 
  size = 'md', 
  className = '', 
  overlay = false,
  text,
  'aria-label': ariaLabel = 'Loading...'
}: LoadingSpinnerProps) {
  const spinnerId = React.useMemo(() => generateId('spinner'), []);
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  };

  // Announce loading state to screen readers
  React.useEffect(() => {
    if (text) {
      announceToScreenReader(text);
    }
  }, [text]);

  const spinner = (
    <div 
      id={spinnerId}
      data-testid="loading-spinner"
      className={`flex items-center justify-center ${className}`}
      role="status"
      aria-label={ariaLabel}
      aria-live="polite"
      aria-atomic="true"
    >
      <Bot 
        className={`${sizeClasses[size]} animate-spin`}
        style={{ 
          transformOrigin: 'center',
          color: colors.primary[500]
        }}
        aria-hidden="true"
      />
      {text && (
        <span className="ml-2 text-sm text-gray-600 sr-only">
          {text}
        </span>
      )}
    </div>
  );

  if (overlay) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
        {spinner}
      </div>
    );
  }

  return spinner;
}